import React from 'react';
import { UseFormReturn, useWatch, Controller } from 'react-hook-form';
import styled from '@emotion/styled';
import { Form, Alert } from 'react-bootstrap';

import { burncenterLabel } from 'helpers/forms';
import { BurnCenterType } from 'types/burnCenter';
import { Roles, RolesLabels } from 'utils/enums';
import useUser from 'hooks/useUser';
import { UserFormValues, DataEntryRoles, ReportingRoles } from './types';

const PlaceholderSelect = styled(Form.Select)`
  &.is-placeholder {
    font-style: italic;
    color: #6c757d; /* Bootstrap gray-600 */
  }
  option[value=""] {
    font-style: italic;
  }
  &:disabled.is-placeholder {
    font-style: italic; /* keep italic even when disabled */
  }
`;

const FormColumn = styled.div`
  width: 100%;
  max-width: 420px;
`;

const FormLabel = styled(Form.Label)`
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0;
`;

// Same text input visual as Burn Center
const formTextStyle = `
  border: 0;
  border-bottom: 1px solid #ced4da;
  border-radius: 0;
`;

const FormText = styled(Form.Control)`
  ${({ as }: { as?: string }) => (as ? '' : formTextStyle)}
`;

const PagePad = styled.div`
  padding-left: 1rem;
  padding-right: 1rem;
`;

const GrantBox = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  width: 100%;
  max-width: 620px;
`;

const AlignControl = styled.div`
  margin-left: 20px;
`;

const InlineControl = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
  margin-left: 20px;
  > .form-select {
    max-width: 340px;
  }
`;

type Props = {
  form: UseFormReturn<UserFormValues>;
  mode: 'create' | 'edit';
  initialValues?: Partial<UserFormValues>;
  onSubmit: (payload: UserFormValues) => Promise<void>;
  cancelHref: string;
  burnCenters: BurnCenterType[];
  centersLoading?: boolean;
  centersError?: Error | null;
  isAdmin?: boolean;
  fixedBurnCenterId?: number | null;
  disallowedRoles?: Array<(typeof Roles)[keyof typeof Roles]>;
  lockDisallowedEditRole?: boolean;
  allowEdit?: boolean;
  onCancel?: () => void;
};

type FieldProps = {
  label: string;
  name: keyof UserFormValues;
  type?: string;
  form: UseFormReturn<UserFormValues>;
  placeholder?: string;
  as?: 'textarea' | undefined;
  required?: boolean;
  disabled?: boolean;
  numericOnly?: boolean;
};

const requiredTag = (required?: boolean) => (required ? (
  <span style={{ marginLeft: 5, fontSize: '1.5rem', color: 'red' }}>*</span>
) : null);

const Field: React.FC<FieldProps> = ({
  label,
  name,
  type = 'text',
  form,
  placeholder,
  as,
  required,
  disabled = false,
  numericOnly = false,
}) => {
  const {
    register,
    formState: { errors },
  } = form;
  const { onChange, ...fieldRegistration } = register(name);
  const error = errors[name]?.message as string | undefined;
  const isCheckbox = type === 'checkbox';

  return (
    <Form.Group className="mb-3" controlId={name as string}>
      {!isCheckbox && (
        <FormLabel>
          {label}
          {requiredTag(required)}
        </FormLabel>
      )}
      {isCheckbox ? (
        <Form.Check
          {...register(name)}
          type="checkbox"
          label={label}
          isInvalid={!!error}
          disabled={disabled}
        />
      ) : (
        <FormText
          {...fieldRegistration}
          type={type}
          placeholder={placeholder}
          isInvalid={!!error}
          disabled={disabled}
          as={as}
          inputMode={numericOnly ? 'numeric' : undefined}
          onChange={(e) => {
            if (numericOnly) {
              e.target.value = e.target.value.replace(/\D/g, '');
            }
            onChange(e);
          }}
        />
      )}
      <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
        {error}
      </Form.Control.Feedback>
    </Form.Group>
  );
};

type SelectRowProps = {
  label: string;
  name: keyof UserFormValues;
  form: UseFormReturn<UserFormValues>;
  children: React.ReactNode;
  defaultValue?: string;
  required?: boolean;
  disabled?: boolean;
};
const SelectRow: React.FC<SelectRowProps> = ({
  label,
  name,
  form,
  children,
  required,
  defaultValue = '',
  disabled = false,
}) => {
  const {
    register,
    formState: { errors },
  } = form;
  const error = errors[name]?.message as string | undefined;
  return (
    <Form.Group className="mb-3" controlId={name as string}>
      <FormLabel>
        {label}
        {requiredTag(required)}
      </FormLabel>
      <Form.Select
        {...register(name)}
        isInvalid={!!error}
        defaultValue={defaultValue}
        disabled={disabled}
      >
        {children}
      </Form.Select>
      <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
        {error}
      </Form.Control.Feedback>
    </Form.Group>
  );
};

type CheckboxRowProps = {
  label: string;
  groupTitle?: string | null;
  name: keyof UserFormValues;
  form: UseFormReturn<UserFormValues>;
  disabled?: boolean;
};
const CheckboxRow: React.FC<CheckboxRowProps> = ({
  label,
  name,
  form,
  disabled = false,
}) => {
  const {
    register,
    formState: { errors },
  } = form;
  const error = errors[name]?.message as string | undefined;
  return (
    <Form.Group className="mb-2" controlId={name as string}>
      <Form.Check
        type="checkbox"
        label={label}
        {...register(name)}
        isInvalid={!!error}
        disabled={disabled}
      />
      <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
        {error}
      </Form.Control.Feedback>
    </Form.Group>
  );
};

const allowedRolesForBdataAdmin: Array<(typeof Roles)[keyof typeof Roles]> = [
  Roles.bdataAdmin,
  Roles.abaAdmin,
  Roles.manager,
  Roles.burnCenterUser,
];

const allowedRolesForBcAdmin: Array<(typeof Roles)[keyof typeof Roles]> = [
  Roles.manager,
  Roles.burnCenterUser,
];

const UserForm: React.FC<Props> = ({
  form,
  initialValues,
  onSubmit,
  mode,
  burnCenters,
  centersLoading = false,
  centersError = null,
  fixedBurnCenterId = undefined,
  disallowedRoles = [],
  lockDisallowedEditRole = true,
  allowEdit = false,
}) => {
  const { handleSubmit, register, setValue } = form;

  const {
    isAdmin: isSuperAdmin, isBdataAmin, isBcAdmin, appUser,
  } = useUser();

  const isEdit = mode === 'edit';
  const isDisabled = isEdit && !allowEdit;

  const isEditingSelf = isEdit
    && !!initialValues?.email
    && !!appUser?.email
    && appUser.email === initialValues.email;

  const allRoleValues = Object.values(Roles) as Array<
    (typeof Roles)[keyof typeof Roles]
  >;
  const disallowedSet = new Set(disallowedRoles);

  let allowedRolesByUser: Array<(typeof Roles)[keyof typeof Roles]> = allRoleValues;

  if (isBdataAmin || isSuperAdmin) {
    allowedRolesByUser = allowedRolesForBdataAdmin;
  } else if (isBcAdmin) {
    allowedRolesByUser = allowedRolesForBcAdmin;
  } else {
    allowedRolesByUser = allRoleValues;
  }

  React.useEffect(() => {
    if (fixedBurnCenterId !== undefined) {
      setValue('burnCenterId', fixedBurnCenterId ?? null, {
        shouldDirty: true,
      });
    }
  }, [fixedBurnCenterId, setValue]);

  const role = useWatch({ control: form.control, name: 'role' });
  const clinicalStudioRole = useWatch({
    control: form.control,
    name: 'clinicalStudioRole',
  });
  const reportingRole = useWatch({
    control: form.control,
    name: 'reportingRole',
  });
  const burnCenterId = useWatch({ control: form.control, name: 'burnCenterId' });

  const roleOptionsForCreate = allowedRolesByUser.filter(
    (r) => !disallowedSet.has(r),
  );

  const isCurrentRoleDisallowed = isEdit && role != null && disallowedSet.has(role);
  const disableRoleSelect = (isCurrentRoleDisallowed && lockDisallowedEditRole) || isEditingSelf;

  let roleOptions = isEdit ? allowedRolesByUser : roleOptionsForCreate;

  if (isEditingSelf && role) {
    roleOptions = [role];
  } else if (isEdit && role && !roleOptions.includes(role)) {
    roleOptions = [...roleOptions, role];
  }

  const showBurnCenter = fixedBurnCenterId === undefined
    && (!role
      || role === Roles.burnCenterUser
      || role === Roles.manager);

  React.useEffect(() => {
    if (!showBurnCenter && fixedBurnCenterId === undefined) {
      setValue('burnCenterId', null, { shouldDirty: true });
    }
  }, [showBurnCenter, fixedBurnCenterId, setValue]);

  const hasSelectedBurnCenter = burnCenterId != null;

  React.useEffect(() => {
    if (!hasSelectedBurnCenter) {
      setValue('canAccessBedEntry', false, { shouldDirty: true });
    }
  }, [hasSelectedBurnCenter, setValue]);

  const [enableDataEntry, setEnableDataEntry] = React.useState(
    !!initialValues?.clinicalStudioRole,
  );
  const [enableReporting, setEnableReporting] = React.useState(
    !!initialValues?.reportingRole,
  );
  React.useEffect(() => {
    if (!enableDataEntry) {
      setValue('clinicalStudioRole', null, {
        shouldDirty: true,
      });
    }
    setValue('enableDataEntry' as any, enableDataEntry, { shouldDirty: true });
  }, [enableDataEntry, setValue, form]);
  React.useEffect(() => {
    if (!enableReporting) {
      setValue('reportingRole', null, {
        shouldDirty: true,
      });
    }
    setValue('enableReporting' as any, enableReporting, { shouldDirty: true });
  }, [enableReporting, setValue, form]);

  const submit = async (raw: UserFormValues) => {
    const payload: UserFormValues = {
      ...raw,
      reportingRole: enableReporting ? raw.reportingRole : null,
      clinicalStudioRole: enableDataEntry ? raw.clinicalStudioRole : null,
      canUpdateTrainingContent: isBdataAmin
        ? raw.canUpdateTrainingContent
        : (initialValues?.canUpdateTrainingContent ?? false),
      burnCenterId:
        fixedBurnCenterId !== undefined
          ? (fixedBurnCenterId ?? null)
          : raw.burnCenterId,
    };
    await onSubmit(payload);
  };

  return (
    <PagePad>
      <Form onSubmit={handleSubmit(submit)} noValidate>
        <FormColumn>
          <Field
            label="Name"
            name="name"
            form={form}
            required
            disabled={isDisabled}
          />
          <Field
            label="Nickname"
            name="nickname"
            form={form}
            disabled={isDisabled}
          />
          {/* Temporarily hidden
          <Field
            label="Title"
            name="title"
            form={form}
            disabled={isDisabled}
          />
          <Field
            label="Credentials"
            name="credentials"
            form={form}
            disabled={isDisabled}
          />
          */}
          <Field
            label="Email"
            name="email"
            type="email"
            form={form}
            required
            disabled={isDisabled}
          />
          {/* Temporarily hidden
          <Field
            label="Phone Number"
            name="phoneNumber"
            type="tel"
            form={form}
            disabled={isDisabled}
          />
          */}

          <SelectRow
            label="Role"
            name="role"
            form={form}
            defaultValue=""
            required
            disabled={isDisabled || disableRoleSelect}
          >
            <option value="" style={{ fontStyle: 'italic' }}>
              Select role
            </option>
            {roleOptions.map((value) => (
              <option key={value} value={value}>
                {RolesLabels[value]}
              </option>
            ))}
          </SelectRow>

          {showBurnCenter && (
            <Form.Group className="mb-3" controlId="burnCenterId">
              <FormLabel>
                Burn Center
                {(['burn_center_user', 'manager'].includes(
                  role ?? '',
                )
                  || fixedBurnCenterId !== undefined) && (
                  <span
                    style={{ marginLeft: 5, fontSize: '1.5rem', color: 'red' }}
                  >
                    *
                  </span>
                )}
              </FormLabel>

              <Controller
                name="burnCenterId"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Select
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(
                        e.target.value === '' ? null : Number(e.target.value),
                      )}
                      isInvalid={!!fieldState.error}
                      disabled={isDisabled}
                    >
                      <option value="" style={{ fontStyle: 'italic' }}>
                        Not assigned
                      </option>
                      {burnCenters.map((bc) => (
                        <option key={bc.id} value={String(bc.id)}>
                          {burncenterLabel(bc)}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{ display: 'block' }}
                    >
                      {fieldState.error?.message}
                    </Form.Control.Feedback>
                  </>
                )}
              />

              {centersLoading && (
                <div className="small text-muted mt-1">
                  Loading burn centers…
                </div>
              )}
              {centersError && (
                <Alert className="mt-2 py-1" variant="warning">
                  Failed to load burn centers
                </Alert>
              )}
            </Form.Group>
          )}
        </FormColumn>
        <GrantBox style={{ marginBottom: 32 }}>
          <FormLabel>Grant Access</FormLabel>
          <AlignControl style={{ marginTop: 12 }}>
            <CheckboxRow
              label="Verification"
              name="canAccessVerification"
              form={form}
              disabled={isDisabled}
            />
            <CheckboxRow
              label="Bed Board Input"
              name="canAccessBedEntry"
              form={form}
              disabled={isDisabled || !hasSelectedBurnCenter}
            />
            {isBdataAmin && (
              <CheckboxRow
                label="Update Training Content"
                name="canUpdateTrainingContent"
                form={form}
                disabled={isDisabled}
              />
            )}
          </AlignControl>

          <InlineControl style={{ marginTop: 20 }}>
            <Form.Check
              type="checkbox"
              id="enable-data-entry"
              label="Data Entry"
              checked={enableDataEntry}
              onChange={(e) => setEnableDataEntry(e.target.checked)}
              disabled={isDisabled}
            />
            <PlaceholderSelect
              {...register('clinicalStudioRole')}
              isInvalid={!!form.formState.errors.clinicalStudioRole}
              defaultValue={initialValues?.clinicalStudioRole ?? ''}
              disabled={isDisabled || !enableDataEntry}
              style={{
                fontStyle: !clinicalStudioRole ? 'italic' : 'normal',
              }}
            >
              <option value="">Select data entry role</option>
              {DataEntryRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </PlaceholderSelect>
          </InlineControl>
          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
            {form.formState.errors.clinicalStudioRole?.message as string}
          </Form.Control.Feedback>

          <InlineControl style={{ marginTop: 12 }}>
            <Form.Check
              type="checkbox"
              id="enable-reporting"
              label="Reporting"
              checked={enableReporting}
              disabled={isDisabled}
              onChange={(e) => setEnableReporting(e.target.checked)}
            />
            <PlaceholderSelect
              {...register('reportingRole')}
              isInvalid={!!form.formState.errors.reportingRole}
              disabled={isDisabled || !enableReporting}
              defaultValue={initialValues?.reportingRole ?? ''}
              style={{
                fontStyle: !reportingRole ? 'italic' : 'normal',
              }}
            >
              <option value="">Select reporting role</option>
              {ReportingRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </PlaceholderSelect>
          </InlineControl>
          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
            {form.formState.errors.reportingRole?.message as string}
          </Form.Control.Feedback>
        </GrantBox>
      </Form>
    </PagePad>
  );
};

export default UserForm;
